import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Alert
} from 'react-bootstrap';
import {
  FaCog,
  FaInfoCircle,
  FaPlus,
  FaGripVertical,
  FaArrowUp,
  FaArrowDown,
  FaTrash,
  FaCheckCircle
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';

function parseLayoutActions(layout) {
  if (!layout?.actions) return [];
  if (typeof layout.actions === 'string') {
    try {
      return JSON.parse(layout.actions);
    } catch (e) {
      console.warn('Failed to parse actions JSON:', e);
      return [];
    }
  }
  if (Array.isArray(layout.actions)) {
    return layout.actions;
  }
  return [];
}

const ConfigureDetailLayoutActionsModal = ({
  show,
  layout,
  objectName,
  ctiEnabled,
  onHide,
  onSaved,
  setGlobalLoading,
  loading = false
}) => {
  const [layoutActions, setLayoutActions] = useState([]);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [detailCustomCandidates, setDetailCustomCandidates] = useState([]);
  const [loadingCustomCandidates, setLoadingCustomCandidates] = useState(false);
  const [phoneFields, setPhoneFields] = useState([]);
  const [loadingPhoneFields, setLoadingPhoneFields] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !layout) {
      return;
    }

    setLayoutActions(parseLayoutActions(layout));
    setError('');
    setPhoneFields([]);

    let cancelled = false;
    (async () => {
      if (!objectName) return;
      try {
        setLoadingTemplates(true);
        const api = new DataApi(`generatedocument/templates/${objectName}`);
        const response = await api.fetchAll();
        if (cancelled) return;
        if (response?.data?.success) {
          setDocumentTemplates(response.data.data || []);
        } else {
          setDocumentTemplates([]);
        }
      } catch (err) {
        console.error('Error fetching document templates:', err);
        if (!cancelled) setDocumentTemplates([]);
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();

    (async () => {
      if (!objectName) return;
      try {
        setLoadingPhoneFields(true);
        const api = new DataApi(`objectconfig/${encodeURIComponent(objectName)}`);
        const response = await api.fetchAll();
        if (cancelled) return;
        if (response?.data?.success) {
          const objectConfig = response.data.data;
          const fields = Array.isArray(objectConfig?.fields) ? objectConfig.fields : [];
          const phoneOnly = fields.filter((f) => {
            const dt = (f?.data_type ?? f?.dataType ?? '').toString().toLowerCase();
            return dt === 'phone';
          });
          setPhoneFields(phoneOnly);
        } else {
          setPhoneFields([]);
        }
      } catch (err) {
        console.error('Error fetching phone fields:', err);
        if (!cancelled) setPhoneFields([]);
      } finally {
        if (!cancelled) setLoadingPhoneFields(false);
      }
    })();

    (async () => {
      if (!objectName) return;
      try {
        setLoadingCustomCandidates(true);
        const api = new DataApi('actions');
        const res = await api.get(`/detail-layout-candidates/${encodeURIComponent(objectName)}`);
        if (cancelled) return;
        if (res?.data?.success) {
          setDetailCustomCandidates(res.data.data || []);
        } else {
          setDetailCustomCandidates([]);
        }
      } catch (err) {
        console.error('Error fetching detail custom actions:', err);
        if (!cancelled) setDetailCustomCandidates([]);
      } finally {
        if (!cancelled) setLoadingCustomCandidates(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, layout, objectName]);

  const handleClose = () => {
    setError('');
    onHide();
  };

  const handleSave = async () => {
    const generateDocActions = layoutActions.filter(
      (a) => typeof a === 'object' && a.type === 'generate_document'
    );

    for (const action of generateDocActions) {
      if (!action.label || action.label.trim() === '') {
        setError('Please provide a label for all Generate Document buttons');
        return;
      }
      if (!action.templateAction) {
        setError('Please select a document template for all Generate Document buttons');
        return;
      }
    }

    const addProductsActions = layoutActions.filter(
      (a) => typeof a === 'object' && a.type === 'add_products'
    );
    for (const action of addProductsActions) {
      if (!action.label || action.label.trim() === '') {
        setError('Add Products label is required');
        return;
      }
    }

    try {
      setGlobalLoading(true);
      setError('');

      if (!layout?.layout_id) {
        setError('Layout ID is missing');
        return;
      }

      const api = new DataApi('objectconfig/layouts');
      const response = await api.update({ actions: layoutActions }, layout.layout_id);

      if (response?.data?.success) {
        onSaved?.();
        handleClose();
      } else {
        setError('Failed to save actions configuration');
      }
    } catch (err) {
      console.error('Error saving actions:', err);
      setError('Failed to save actions: ' + (err.message || 'Unknown error'));
    } finally {
      setGlobalLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      backdrop={false}
      keyboard={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCog className="me-2" />
          Configure Detail Layout Actions
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            {error}
          </Alert>
        )}
        {layout && (
          <>
            <Alert variant="info" className="mb-3">
              <FaInfoCircle className="me-2" />
              Configure which action buttons (Edit, Clone, New, Delete, Generate Document) should appear in the
              detail view header for this layout.
            </Alert>

            <Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>Layout:</strong> {layout.layout_name}
                </Form.Label>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Configure Actions & Order</Form.Label>
                <Form.Text className="d-block mb-3 text-muted">
                  Add actions and arrange their order. Actions will appear in the detail view header in the order
                  shown below.
                </Form.Text>

                <div className="border rounded p-3 mb-3 bg-light">
                  <Form.Label className="fw-bold mb-2">Quick Add Actions</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {['edit', 'clone', 'new', 'delete', 'email', 'call', 'whatsapp'].map((action) => (
                      <Button
                        key={action}
                        variant={
                          layoutActions.some((a) => (typeof a === 'string' ? a === action : a.type === action))
                            ? 'primary'
                            : 'outline-primary'
                        }
                        size="sm"
                        disabled={
                          (action === 'call' && !ctiEnabled) ||
                          (action === 'whatsapp' && phoneFields.length === 0 && !loadingPhoneFields)
                        }
                        title={
                          action === 'call' && !ctiEnabled
                            ? 'Enable TeleCMI or Callyzer package to use Call action'
                            : action === 'whatsapp' && phoneFields.length === 0 && !loadingPhoneFields
                              ? 'No phone fields found for this object'
                            : ''
                        }
                        onClick={() => {
                          if (action === 'call' && !ctiEnabled) {
                            return;
                          }
                          if (action === 'whatsapp' && phoneFields.length === 0 && !loadingPhoneFields) {
                            return;
                          }
                          if (layoutActions.some((a) => (typeof a === 'string' ? a === action : a.type === action))) {
                            setLayoutActions(
                              layoutActions.filter((a) => (typeof a === 'string' ? a !== action : a.type !== action))
                            );
                          } else {
                            if (action === 'whatsapp') {
                              const defaultPhoneFields = phoneFields
                                .filter((f) => {
                                  const name = (f?.field_name ?? f?.fieldName ?? '').toString().toLowerCase();
                                  return name === 'phone' || name === 'mobile' || name === 'whatsapp_number';
                                })
                                .slice(0, 1)
                                .map((f) => (f?.field_name ?? f?.fieldName ?? '').toString())
                                .filter(Boolean);
                              setLayoutActions([
                                ...layoutActions,
                                { type: 'whatsapp', label: 'WhatsApp', phone_fields: defaultPhoneFields }
                              ]);
                            } else {
                              setLayoutActions([...layoutActions, action]);
                            }
                          }
                        }}
                      >
                        {(() => {
                          if (action === 'email') return 'Send Email';
                          if (action === 'call') return ctiEnabled ? 'Call' : 'Call (CTI required)';
                          if (action === 'whatsapp') return 'WhatsApp';
                          return action.charAt(0).toUpperCase() + action.slice(1);
                        })()}
                      </Button>
                    ))}
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => {
                        setLayoutActions([
                          ...layoutActions,
                          {
                            type: 'generate_document',
                            label: '',
                            templateAction: null,
                            createAttachment: false
                          }
                        ]);
                      }}
                    >
                      <FaPlus className="me-1" />
                      Generate Document
                    </Button>
                    {['opportunity', 'quote', 'order'].includes(objectName?.toLowerCase()) && (
                      <Button
                        variant={
                          layoutActions.some((a) =>
                            typeof a === 'string' ? a === 'add_products' : a.type === 'add_products'
                          )
                            ? 'primary'
                            : 'outline-primary'
                        }
                        size="sm"
                        onClick={() => {
                          if (
                            layoutActions.some((a) =>
                              typeof a === 'string' ? a === 'add_products' : a.type === 'add_products'
                            )
                          ) {
                            setLayoutActions(
                              layoutActions.filter((a) =>
                                typeof a === 'string' ? a !== 'add_products' : a.type !== 'add_products'
                              )
                            );
                          } else {
                            setLayoutActions([...layoutActions, { type: 'add_products', label: 'Add Products' }]);
                          }
                        }}
                      >
                        Add Products
                      </Button>
                    )}
                    {objectName?.toLowerCase() === 'quote' && (
                      <Button
                        variant={
                          layoutActions.some((a) =>
                            typeof a === 'string' ? a === 'sync_products' : a.type === 'sync_products'
                          )
                            ? 'primary'
                            : 'outline-primary'
                        }
                        size="sm"
                        onClick={() => {
                          if (
                            layoutActions.some((a) =>
                              typeof a === 'string' ? a === 'sync_products' : a.type === 'sync_products'
                            )
                          ) {
                            setLayoutActions(
                              layoutActions.filter((a) =>
                                typeof a === 'string' ? a !== 'sync_products' : a.type !== 'sync_products'
                              )
                            );
                          } else {
                            setLayoutActions([...layoutActions, { type: 'sync_products', label: 'Sync Products' }]);
                          }
                        }}
                      >
                        Sync Products
                      </Button>
                    )}
                    {objectName?.toLowerCase() === 'lead' && (
                      <Button
                        variant={
                          layoutActions.some((a) =>
                            typeof a === 'string' ? a === 'convert_lead' : a.type === 'convert_lead'
                          )
                            ? 'primary'
                            : 'outline-primary'
                        }
                        size="sm"
                        onClick={() => {
                          if (
                            layoutActions.some((a) =>
                              typeof a === 'string' ? a === 'convert_lead' : a.type === 'convert_lead'
                            )
                          ) {
                            setLayoutActions(
                              layoutActions.filter((a) =>
                                typeof a === 'string' ? a !== 'convert_lead' : a.type !== 'convert_lead'
                              )
                            );
                          } else {
                            setLayoutActions([...layoutActions, 'convert_lead']);
                          }
                        }}
                      >
                        Convert Lead
                      </Button>
                    )}
                  </div>
                </div>

                {objectName && (
                  <div className="border rounded p-3 mb-3 bg-light">
                    <Form.Label className="fw-bold mb-2">Custom Actions</Form.Label>
                    <Form.Text className="d-block mb-3 text-muted small">
                      From the Actions catalog with <strong>action_type</strong>{' '}
                      <code>detail_action</code> or <code>detail_Actions</code>, where <strong>target_object</strong>{' '}
                      matches <code>{objectName}</code>. Click to add or remove from the layout.
                    </Form.Text>
                    <div className="d-flex flex-wrap gap-2">
                      {loadingCustomCandidates ? (
                        <span className="small text-muted">Loading custom actions…</span>
                      ) : detailCustomCandidates.length === 0 ? (
                        <span className="small text-muted">
                          No custom actions for <strong>{objectName}</strong>. Create one in Action Manager with{' '}
                          <code>target_object</code> set to this object name.
                        </span>
                      ) : (
                        detailCustomCandidates.map((row) => (
                          <Button
                            key={row.id || row.action_name}
                            variant={
                              layoutActions.some(
                                (a) =>
                                  typeof a === 'object' &&
                                  a.type === 'custom_action' &&
                                  a.action_name === row.action_name
                              )
                                ? 'primary'
                                : 'outline-primary'
                            }
                            size="sm"
                            onClick={() => {
                              const on = layoutActions.some(
                                (a) =>
                                  typeof a === 'object' &&
                                  a.type === 'custom_action' &&
                                  a.action_name === row.action_name
                              );
                              if (on) {
                                setLayoutActions(
                                  layoutActions.filter(
                                    (a) =>
                                      !(
                                        typeof a === 'object' &&
                                        a.type === 'custom_action' &&
                                        a.action_name === row.action_name
                                      )
                                  )
                                );
                              } else {
                                setLayoutActions([
                                  ...layoutActions,
                                  { type: 'custom_action', action_name: row.action_name }
                                ]);
                              }
                            }}
                            title={
                              row.target_url
                                ? `URL: ${row.target_url} (supports {{recordId}}, {{objectName}})`
                                : row.action_name
                            }
                          >
                            {row.icon ? <i className={`${row.icon} me-1`} aria-hidden /> : null}
                            {row.action_label || row.action_name}
                          </Button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <Form.Label className="fw-bold">Action Order (Drag to reorder)</Form.Label>
                  <div className="border rounded p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {layoutActions.length === 0 ? (
                      <div className="text-center text-muted p-3">No actions configured. Add actions above.</div>
                    ) : (
                      layoutActions.map((action, idx) => {
                        const isGenerateDoc = typeof action === 'object' && action.type === 'generate_document';
                        const isWhatsApp = typeof action === 'object' && action.type === 'whatsapp';
                        const isAddProducts =
                          (typeof action === 'string' && action === 'add_products') ||
                          (typeof action === 'object' && action.type === 'add_products');
                        const isSyncProducts =
                          (typeof action === 'string' && action === 'sync_products') ||
                          (typeof action === 'object' && action.type === 'sync_products');
                        const isConvertLead =
                          (typeof action === 'string' && action === 'convert_lead') ||
                          (typeof action === 'object' && action.type === 'convert_lead');
                        const isEmail = typeof action === 'string' && action === 'email';
                        const isCustomAction = typeof action === 'object' && action.type === 'custom_action';
                        const actionLabel = (() => {
                          if (typeof action === 'string') {
                            return isAddProducts
                              ? 'Add Products'
                              : isSyncProducts
                                ? 'Sync Products'
                                : isConvertLead
                                  ? 'Convert Lead'
                                  : isEmail
                                    ? 'Send Email'
                                    : action.charAt(0).toUpperCase() + action.slice(1);
                          }
                          if (isCustomAction) {
                            return (
                              action.action_label ||
                              detailCustomCandidates.find((c) => c.action_name === action.action_name)
                                ?.action_label ||
                              action.action_name
                            );
                          }
                          if (isGenerateDoc) {
                            return action.label || 'Generate Document';
                          }
                          if (isWhatsApp) {
                            return action.label || 'WhatsApp';
                          }
                          return (
                            action.label ||
                            (action.type === 'add_products'
                              ? 'Add Products'
                              : action.type === 'sync_products'
                                ? 'Sync Products'
                                : action.type === 'convert_lead'
                                  ? 'Convert Lead'
                                  : action.type)
                          );
                        })();

                        return (
                          <div key={idx} className="border rounded p-2 mb-2 bg-light">
                            <div className="d-flex align-items-start justify-content-between mb-2">
                              <div className="d-flex align-items-center flex-grow-1">
                                <FaGripVertical className="me-2 text-muted" style={{ cursor: 'grab' }} />
                                <div>
                                  <strong>{actionLabel}</strong>
                                  {isGenerateDoc && action.templateAction && (
                                    <Badge bg="info" className="ms-2">
                                      {documentTemplates.find((t) => t.action === action.templateAction)?.name ||
                                        action.templateAction}
                                    </Badge>
                                  )}
                                  {isGenerateDoc && action.createAttachment && (
                                    <Badge bg="success" className="ms-1">
                                      Attach
                                    </Badge>
                                  )}
                                  {isCustomAction && (
                                    <Badge bg="secondary" className="ms-2">
                                      custom
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    if (idx > 0) {
                                      const newActions = [...layoutActions];
                                      [newActions[idx - 1], newActions[idx]] = [newActions[idx], newActions[idx - 1]];
                                      setLayoutActions(newActions);
                                    }
                                  }}
                                  disabled={idx === 0}
                                  title="Move Up"
                                >
                                  <FaArrowUp />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    if (idx < layoutActions.length - 1) {
                                      const newActions = [...layoutActions];
                                      [newActions[idx], newActions[idx + 1]] = [newActions[idx + 1], newActions[idx]];
                                      setLayoutActions(newActions);
                                    }
                                  }}
                                  disabled={idx === layoutActions.length - 1}
                                  title="Move Down"
                                >
                                  <FaArrowDown />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    setLayoutActions(layoutActions.filter((_, i) => i !== idx));
                                  }}
                                  title="Remove"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </div>

                            {isGenerateDoc && (
                              <div className="ms-4 mt-2 border-top pt-2">
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Button Label</Form.Label>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="e.g., Generate Quote"
                                        value={action.label || ''}
                                        onChange={(e) => {
                                          const newLabel = e.target.value;
                                          setLayoutActions(
                                            layoutActions.map((a, i) => {
                                              if (i === idx && typeof a === 'object' && a.type === 'generate_document') {
                                                return { ...a, label: newLabel };
                                              }
                                              return a;
                                            })
                                          );
                                        }}
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Template</Form.Label>
                                      {loadingTemplates ? (
                                        <div className="text-muted small">Loading...</div>
                                      ) : documentTemplates.length === 0 ? (
                                        <Alert variant="warning" className="mt-0 mb-0 py-1">
                                          <small>No templates found</small>
                                        </Alert>
                                      ) : (
                                        <Form.Select
                                          size="sm"
                                          value={action.templateAction || ''}
                                          onChange={(e) => {
                                            const templateAction = e.target.value;
                                            setLayoutActions(
                                              layoutActions.map((a, i) => {
                                                if (i === idx && typeof a === 'object' && a.type === 'generate_document') {
                                                  return { ...a, templateAction };
                                                }
                                                return a;
                                              })
                                            );
                                          }}
                                        >
                                          <option value="">Select template...</option>
                                          {documentTemplates.map((template) => (
                                            <option key={template.id} value={template.action}>
                                              {template.name || template.action}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      )}
                                    </Form.Group>
                                  </Col>
                                </Row>
                                <Form.Check
                                  type="checkbox"
                                  id={`create-attachment-${idx}`}
                                  label="Create Attachment"
                                  checked={action.createAttachment || false}
                                  onChange={(e) => {
                                    setLayoutActions(
                                      layoutActions.map((a, i) => {
                                        if (i === idx && typeof a === 'object' && a.type === 'generate_document') {
                                          return { ...a, createAttachment: e.target.checked };
                                        }
                                        return a;
                                      })
                                    );
                                  }}
                                  className="small"
                                />
                              </div>
                            )}
                            {isWhatsApp && (
                              <div className="ms-4 mt-2 border-top pt-2">
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Button Label</Form.Label>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="e.g., WhatsApp"
                                        value={action.label || 'WhatsApp'}
                                        onChange={(e) => {
                                          const newLabel = e.target.value;
                                          setLayoutActions(
                                            layoutActions.map((a, i) => {
                                              if (i === idx && typeof a === 'object' && a.type === 'whatsapp') {
                                                return { ...a, label: newLabel };
                                              }
                                              return a;
                                            })
                                          );
                                        }}
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Phone Fields</Form.Label>
                                      {loadingPhoneFields ? (
                                        <div className="text-muted small">Loading...</div>
                                      ) : phoneFields.length === 0 ? (
                                        <Alert variant="warning" className="mt-0 mb-0 py-1">
                                          <small>No phone fields found</small>
                                        </Alert>
                                      ) : (
                                        <MultiSelectDropdown
                                          selectedValues={Array.isArray(action.phone_fields) ? action.phone_fields : []}
                                          picklistValues={phoneFields
                                            .map((f) => {
                                              const key = (f?.field_name ?? f?.fieldName ?? '').toString();
                                              if (!key) return null;
                                              const value = (f?.field_label ?? f?.label ?? key).toString();
                                              return { key, value };
                                            })
                                            .filter(Boolean)}
                                          onChange={(values) => {
                                            setLayoutActions(
                                              layoutActions.map((a, i) => {
                                                if (i !== idx) return a;
                                                if (!(typeof a === 'object' && a.type === 'whatsapp')) return a;
                                                return { ...a, phone_fields: Array.isArray(values) ? values : [] };
                                              })
                                            );
                                          }}
                                          placeholder="Select phone fields..."
                                        />
                                      )}
                                      <Form.Text className="text-muted small">
                                        Only <strong>phone</strong> datatype fields are allowed.
                                      </Form.Text>
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </div>
                            )}
                            {(isAddProducts || isSyncProducts) && (
                              <div className="ms-4 mt-2 border-top pt-2">
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Button Label</Form.Label>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder={isAddProducts ? 'e.g., Add Items' : 'e.g., Sync Items'}
                                        value={(() => {
                                          if (typeof action !== 'object') return '';
                                          if (action.label && action.label.trim() !== '') return action.label;
                                          return isAddProducts ? 'Add Products' : 'Sync Products';
                                        })()}
                                        onChange={(e) => {
                                          const newLabel = e.target.value;
                                          setLayoutActions(
                                            layoutActions.map((a, i) => {
                                              if (i !== idx) return a;
                                              if (typeof a === 'object') {
                                                return { ...a, label: newLabel };
                                              }
                                              const type = a === 'add_products' ? 'add_products' : 'sync_products';
                                              return { type, label: newLabel };
                                            })
                                          );
                                        }}
                                        onBlur={(e) => {
                                          const newLabel = e.target.value.trim();
                                          if (newLabel) return;
                                          const fallbackLabel = isAddProducts ? 'Add Products' : 'Sync Products';
                                          setLayoutActions(
                                            layoutActions.map((a, i) => {
                                              if (i !== idx) return a;
                                              if (typeof a === 'object') {
                                                return { ...a, label: fallbackLabel };
                                              }
                                              const type = a === 'add_products' ? 'add_products' : 'sync_products';
                                              return { type, label: fallbackLabel };
                                            })
                                          );
                                        }}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </div>
                            )}
                            {isCustomAction && (
                              <div className="ms-4 mt-2 border-top pt-2 small text-muted">
                                Defined in Actions · <code>{action.action_name}</code>
                                {detailCustomCandidates.find((c) => c.action_name === action.action_name)?.target_url && (
                                  <span className="d-block mt-1 text-truncate">
                                    {
                                      detailCustomCandidates.find((c) => c.action_name === action.action_name)
                                        .target_url
                                    }
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </Form.Group>

              {layoutActions.length === 0 && (
                <Alert variant="warning">
                  <FaInfoCircle className="me-2" />
                  If no actions are selected, default actions (Edit, New) will be used.
                </Alert>
              )}

              {layoutActions.length > 0 && (
                <div className="mt-3">
                  <strong>Selected Actions:</strong>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {layoutActions.map((action, idx) => {
                      if (typeof action === 'object' && action.type === 'custom_action') {
                        const label =
                          action.action_label ||
                          detailCustomCandidates.find((c) => c.action_name === action.action_name)?.action_label ||
                          action.action_name;
                        return (
                          <Badge key={idx} bg="secondary" className="p-2">
                            {label}
                          </Badge>
                        );
                      }
                      if (typeof action === 'object' && action.type === 'generate_document') {
                        const template = documentTemplates.find((t) => t.action === action.templateAction);
                        return (
                          <Badge key={idx} bg="success" className="p-2">
                            {action.label || 'Generate Document'}
                            {template && <span className="ms-1">({template.name || template.action})</span>}
                            {action.createAttachment && (
                              <span className="ms-1">
                                <FaCheckCircle className="me-1" />
                                (Attach)
                              </span>
                            )}
                          </Badge>
                        );
                      }
                      if (typeof action === 'object' && action.type === 'whatsapp') {
                        return (
                          <Badge key={idx} bg="success" className="p-2">
                            {action.label || 'WhatsApp'}
                          </Badge>
                        );
                      }
                      const isAddProducts =
                        (typeof action === 'string' && action === 'add_products') ||
                        (typeof action === 'object' && action.type === 'add_products');
                      const isSyncProducts =
                        (typeof action === 'string' && action === 'sync_products') ||
                        (typeof action === 'object' && action.type === 'sync_products');
                      const label =
                        typeof action === 'string'
                          ? isAddProducts
                            ? 'Add Products'
                            : isSyncProducts
                              ? 'Sync Products'
                              : action.charAt(0).toUpperCase() + action.slice(1)
                          : action.label ||
                            (action.type === 'add_products'
                              ? 'Add Products'
                              : action.type === 'sync_products'
                                ? 'Sync Products'
                                : action.type?.charAt(0).toUpperCase() + action.type?.slice(1));
                      return (
                        <Badge key={idx} bg="primary" className="p-2">
                          {label}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Actions'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigureDetailLayoutActionsModal;
